import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyRoleAssignmentContext } from '../../../../../../context/CompanyRoleAssignmentContext';
import {
  CompanyRoleAssignmentMutations,
  DeleteCompanyRoleAssignmentResponse,
  DeleteCompanyRoleAssignmentVariables,
  LeaveCompanyResponse,
  LeaveCompanyVariables,
} from '../../../../../../graphql/companyRoleAssignment.graphql';
import { AlertModal } from '../../../../../components/Modal';
import { CompanyRoleAssignment } from '../../../../../../types/company_role_assignment/companyRoleAssignment';
import { CompanyRoleAssignmentInput } from '../../../../../../types/company_role_assignment/companyRoleAssignment.input';
import { useToast } from '../../../../../../providers/ToastProvider';
import { Button, ButtonVariant, CancelButton } from '../../../../../components/Button';
import { ToastType } from '../../../../../components/Toast';
import { Routing } from '../../../../../../util/routes.util';
import useMutation from '../../../../../../hooks/useMutation';
import { UserContext } from '../../../../../../context/UserContext';
import { translate } from '../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { CompanyContext } from '../../../../../../context/CompanyContext';

interface DeleteCompanyRoleAssignmentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  assignment: CompanyRoleAssignment;
}

export default function DeleteCompanyRoleAssignmentModal({
  open,
  setOpen,
  assignment,
}: DeleteCompanyRoleAssignmentModalProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { currentUser } = useContext(UserContext);
  const { companyRoles, setCompanyRoles } = useContext(CompanyRoleAssignmentContext);
  const { companies, setCompanies } = useContext(CompanyContext);

  const isLeave = assignment.userId === currentUser?.id;

  const handleRemoveCompanyRoleAssignment = (assignment: CompanyRoleAssignment) => {
    const forUser = companyRoles.get(assignment.userId) || [];
    companyRoles.set(
      assignment.userId,
      forUser.filter(a => a.id !== assignment.id),
    );
    setCompanyRoles(new Map(companyRoles));
    navigate(Routing.settings.users.overview());
  };

  const [remove, { loading: removeLoading }] = useMutation<
    DeleteCompanyRoleAssignmentResponse,
    DeleteCompanyRoleAssignmentVariables
  >(
    CompanyRoleAssignmentMutations.remove,

    {
      context: {
        headers: {
          'X-Company': assignment.companyId,
        },
      },
      variables: {
        companyRoleAssignment: new CompanyRoleAssignmentInput(assignment).forDelete(),
      },
      onCompleted: response => {
        const forUser = companyRoles.get(response.deleteCompanyRoleAssignment.userId) || [];
        companyRoles.set(
          response.deleteCompanyRoleAssignment.userId,
          forUser.filter(a => a.id !== response.deleteCompanyRoleAssignment.id),
        );
        setCompanyRoles(new Map(companyRoles));
        if (isLeave) {
          companies.delete(response.deleteCompanyRoleAssignment.companyId);
          setCompanies(new Map(companies));
        }
        navigate(Routing.settings.users.overview());
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.removedUserFromCompanySuccessfullyToastTitle }),
        });
      },
    },
  );

  const [leave, { loading: leaveLoading }] = useMutation<LeaveCompanyResponse, LeaveCompanyVariables>(
    CompanyRoleAssignmentMutations.leave,
    {
      context: {
        headers: {
          'X-Company': assignment.companyId,
        },
      },
      variables: {
        roleId: assignment.id,
      },
      onCompleted: response => {
        handleRemoveCompanyRoleAssignment(response.leaveCompany);
        companies.delete(assignment.companyId);
        setCompanies(new Map(companies));
        setOpen(false);
        navigate(Routing.companies.overview());
        showToast({
          type: ToastType.success,
          title: translate({ key: TranslationKey.leftCompanySuccessfullyToastTitle }),
        });
      },
    },
  );

  const loading = removeLoading || leaveLoading;

  return (
    <AlertModal
      title={translate({
        key: isLeave
          ? TranslationKey.deleteCompanyRoleAssignmentLeaveModalTitle
          : TranslationKey.deleteCompanyRoleAssignmentRemoveUserModalTitle,
      })}
      open={open}
      setOpen={setOpen}
    >
      <div className='grid gap-4'>
        <p className='subtitle'>
          {translate({
            key: isLeave
              ? TranslationKey.deleteCompanyRoleAssignmentLeaveModalSubtitle
              : TranslationKey.deleteCompanyRoleAssignmentRemoveUserModalSubtitle,
          })}
        </p>
        <div className='flex justify-end gap-2'>
          <CancelButton onClick={() => setOpen(false)} disabled={loading} />
          <Button
            variant={ButtonVariant.danger}
            onClick={() => (isLeave ? leave() : remove())}
            text={translate({ key: isLeave ? TranslationKey.leave : TranslationKey.remove })}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
